import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fiberAPI, oltAPI, customerAPI, deviceAPI, settingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  GitBranch, Plus, Edit2, Trash2, X, Search,
  ChevronDown, Wifi, WifiOff,
  Users, RefreshCw, Server, AlertCircle, User, Download, Link2,
  RadioTower, HardHat, ShieldCheck,
} from 'lucide-react';
import GeoMapPanel from '../components/fiber/GeoMapPanel';
import { ratioMax } from '../components/fiber/FiberTree';
import useDarkReady from '../theme/useDarkReady';

// ─── PON Port Modal ───────────────────────────────────────────────────────────
function PONPortModal({ ponPort, onClose, onSaved }) {
  const [form, setForm] = useState({
    port:        ponPort?.port        ?? '',
    name:        ponPort?.name        || '',
    description: ponPort?.description || '',
    isActive:    ponPort?.isActive    ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isEdit = ponPort?.inConfig;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.port === '' || form.port == null) { toast.error('Port number is required'); return; }
    setSaving(true);
    try {
      if (isEdit) await fiberAPI.updatePONPort(ponPort.port, form);
      else        await fiberAPI.createPONPort(form);
      toast.success(isEdit ? 'PON port updated' : 'PON port added');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit PON Port' : 'Add PON Port'}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Port Number <span className="required">*</span></label>
              <input className="form-control" type="number" min="0" value={form.port} required
                disabled={isEdit}
                onChange={e => set('port', +e.target.value)}
                placeholder="e.g. 0, 1, 2…" />
            </div>
            <div className="form-group">
              <label className="form-label">Name / Label</label>
              <input className="form-control" value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g. PON-0, Main-Line" />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <input className="form-control" value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Optional description" />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '.85rem' }}>
                <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
                Active
              </label>
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Update' : 'Add PON Port'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── PON Ports manager modal — a lightweight admin utility folded out of the map view
// itself (unlike map.html, our OLT has real PON-port config that isn't just a map node) ──
function PonPortsManagerModal({ ponPorts, loading, onClose, onAdd, onEdit, onDelete }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h3>Manage PON Ports</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button className="btn btn-primary btn-sm" onClick={onAdd}>
              <Plus size={13} style={{ marginRight: 5 }} />Add PON Port
            </button>
          </div>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading…
            </div>
          ) : ponPorts.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
              <Server size={32} style={{ marginBottom: 10 }} />
              <p>No PON ports configured yet.</p>
            </div>
          ) : (
            <div className="table-container" style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>Port #</th>
                    <th>Name</th>
                    <th>Splitters</th>
                    <th>ONUs</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ponPorts.map(p => (
                    <tr key={p.port}>
                      <td style={{ fontWeight: 700 }}>
                        <span className="badge" style={{ background: 'var(--cb-dbeafe)', color: 'var(--c-1e40af)', fontSize: '.82rem' }}>
                          PON {p.port}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{p.name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td>{p.splitterCount}</td>
                      <td>{p.onuCount}</td>
                      <td>
                        {p.inConfig ? (
                          <span className={`badge badge-${p.isActive ? 'active' : 'danger'}`}>
                            {p.isActive ? 'Active' : 'Inactive'}
                          </span>
                        ) : (
                          <span className="badge" style={{ background: 'var(--cb-fef3c7)', color: 'var(--c-92400e)' }}>Used (not in config)</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {p.inConfig ? (
                            <>
                              <button className="btn btn-secondary btn-sm" style={{ padding: '3px 8px' }} onClick={() => onEdit(p)}>
                                <Edit2 size={12} />
                              </button>
                              <button className="btn btn-danger btn-sm" style={{ padding: '3px 8px' }} onClick={() => onDelete(p)}>
                                <Trash2 size={12} />
                              </button>
                            </>
                          ) : (
                            <button className="btn btn-primary btn-sm" style={{ padding: '3px 10px', fontSize: '.75rem' }} onClick={() => onEdit(p)}>
                              <Plus size={11} style={{ marginRight: 4 }} />Add to Config
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Splitter Modal ───────────────────────────────────────────────────────────
const RATIOS = ['1:2','1:4','1:8','1:16','1:32','1:64'];

function SplitterModal({ splitter, splitters = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    name:      splitter?.name      || '',
    ponSlot:   splitter?.ponSlot   ?? 0,
    ponPort:   splitter?.ponPort   ?? 0,
    ratio:     splitter?.ratio     || '1:8',
    location:  splitter?.location  || '',
    address:   splitter?.address   || '',
    notes:     splitter?.notes     || '',
    level:     splitter?.level     || 1,
    parentSplitter: splitter?.parentSplitter?._id || splitter?.parentSplitter || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Level 2 links to a Level 1 parent; Level 3 links to a Level 2 parent
  const parentOptions = splitters.filter(s =>
    (s.level || 1) === form.level - 1 && s._id !== splitter?._id
  );
  const selectedParent = splitters.find(s => s._id === form.parentSplitter);

  const setLevel = (lvl) => setForm(f => ({ ...f, level: lvl, parentSplitter: '' }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.level > 1 && !form.parentSplitter) {
      toast.error(`Select a Level ${form.level - 1} parent splitter to link with`);
      return;
    }
    const payload = {
      ...form,
      parentSplitter: form.level > 1 ? form.parentSplitter : null,
      // Child splitters inherit the PON port from their parent chain
      ponPort: selectedParent ? selectedParent.ponPort : form.ponPort,
      ponSlot: selectedParent ? (selectedParent.ponSlot ?? 0) : form.ponSlot,
    };
    setSaving(true);
    try {
      if (splitter?._id) await fiberAPI.updateSplitter(splitter._id, payload);
      else               await fiberAPI.createSplitter(payload);
      toast.success(splitter?._id ? 'Splitter updated' : 'Splitter added');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3>{splitter?._id ? 'Edit Splitter' : 'Add Splitter'}</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Name / Label <span className="required">*</span></label>
                <input className="form-control" value={form.name} required
                  onChange={e => set('name', e.target.value)} placeholder="e.g. JB-01, Splitter-A" />
              </div>
              <div className="form-group">
                <label className="form-label">Ratio <span className="required">*</span></label>
                <select className="form-control" value={form.ratio} onChange={e => set('ratio', e.target.value)}>
                  {RATIOS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Splitter Level <span className="required">*</span></label>
              <select className="form-control" value={form.level} onChange={e => setLevel(+e.target.value)}>
                <option value={1}>Level 1 — connected to OLT PON port</option>
                <option value={2}>Level 2 — connected to a Level 1 splitter</option>
                <option value={3}>Level 3 — connected to a Level 2 splitter</option>
              </select>
            </div>
            {form.level > 1 && (
              <div className="form-group">
                <label className="form-label">Parent Splitter (Level {form.level - 1}) <span className="required">*</span></label>
                <select className="form-control" value={form.parentSplitter}
                  onChange={e => set('parentSplitter', e.target.value)} required>
                  <option value="">— Select Level {form.level - 1} splitter —</option>
                  {parentOptions.map(s => (
                    <option key={s._id} value={s._id}>
                      {s.name} (PON {s.ponPort} · {s.ratio}{s.location ? ` · ${s.location}` : ''})
                    </option>
                  ))}
                </select>
                {parentOptions.length === 0 && (
                  <div style={{ fontSize: '.75rem', color: 'var(--c-dc2626)', marginTop: 4 }}>
                    No Level {form.level - 1} splitters exist yet — add one first.
                  </div>
                )}
              </div>
            )}
            {form.level > 1 ? (
              <div className="form-group">
                <label className="form-label">PON Port</label>
                <input className="form-control" type="number" disabled
                  value={selectedParent ? selectedParent.ponPort : ''}
                  placeholder="Inherited from parent splitter" />
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Inherited automatically from the parent splitter's PON port.
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">PON Port <span className="required">*</span></label>
                <input className="form-control" type="number" min="0" value={form.ponPort} required
                  onChange={e => set('ponPort', +e.target.value)} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Location / Area</label>
              <input className="form-control" value={form.location}
                onChange={e => set('location', e.target.value)} placeholder="e.g. Main Road, Near Water Tank" />
            </div>
            <div className="form-group">
              <label className="form-label">Full Address</label>
              <input className="form-control" value={form.address}
                onChange={e => set('address', e.target.value)} placeholder="Pole / building address" />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.notes}
                onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : splitter?._id ? 'Update' : 'Add Splitter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Splitter Picker with full/usage indicators ───────────────────────────────
function SplitterPicker({ splitters, splitterUsage, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = splitters.find(s => s._id === value);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="form-control"
        style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
      >
        <span>
          {selected
            ? <><strong>{selected.name}</strong> <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>(PON {selected.ponPort} · {selected.ratio})</span></>
            : <span style={{ color: 'var(--text-muted)' }}>— None —</span>}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, marginLeft: 6 }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 300,
          background: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.15)', maxHeight: 240, overflowY: 'auto',
        }}>
          <div
            onClick={() => { onChange(''); setOpen(false); }}
            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '.82rem', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
          >
            — None —
          </div>
          {splitters.map(s => {
            const max = ratioMax(s.ratio);
            const usedPorts = (splitterUsage[s._id] || []);
            const used = usedPorts.length;
            const isFull = used >= max;
            return (
              <div
                key={s._id}
                onClick={() => { onChange(s._id); setOpen(false); }}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: '.82rem',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  color: isFull ? 'var(--c-dc2626)' : 'inherit',
                }}
                onMouseEnter={e => e.currentTarget.style.background = isFull ? 'var(--cb-fff1f2)' : 'var(--bg)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <span>
                  <span style={{ fontWeight: 600 }}>{s.name}</span>
                  <span style={{ color: isFull ? 'var(--c-ef4444)' : 'var(--text-muted)', fontSize: '.76rem', marginLeft: 6 }}>
                    (PON {s.ponPort} · {s.ratio} · L{s.level || 1})
                  </span>
                </span>
                {isFull ? (
                  <span style={{ background: 'var(--cb-fee2e2)', color: 'var(--c-dc2626)', borderRadius: 4, padding: '1px 7px', fontSize: '.7rem', fontWeight: 700, flexShrink: 0 }}>
                    FULL
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)', fontSize: '.74rem', flexShrink: 0 }}>{used}/{max}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Map ONU Modal ────────────────────────────────────────────────────────────
function MapModal({ onu, splitters, existingMap, mappings, defaultSplitterId, liveOnus = [], onClose, onSaved }) {
  const [customerId,   setCustomerId]   = useState(existingMap?.customerId?._id || '');
  const [customerName, setCustomerName] = useState(existingMap?.customerId?.name || '');
  const [splitterId,   setSplitterId]   = useState(existingMap?.splitterId?._id || defaultSplitterId || '');
  const [splitterPort, setSplitterPort] = useState(() => {
    if (existingMap?.splitterPort) return String(existingMap.splitterPort);
    const initSid = defaultSplitterId;
    if (!initSid) return '';
    const sp = splitters.find(s => s._id === initSid);
    const max = sp ? ratioMax(sp.ratio) : 64;
    const used = new Set(
      (mappings || [])
        .filter(m => (m.splitterId?._id || m.splitterId) === initSid && m.splitterPort)
        .map(m => Number(m.splitterPort))
    );
    for (let p = 1; p <= max; p++) {
      if (!used.has(p)) return String(p);
    }
    return '';
  });
  const [notes,        setNotes]        = useState(existingMap?.notes || '');
  const [search,       setSearch]       = useState('');
  const [results,      setResults]      = useState([]);
  const [searching,    setSearching]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const debounceRef = useRef(null);

  const preSerial = onu.serialNumber || onu.id || onu._serial || '';
  // Label-first entry: pick a live ONU by its label and the serial is fetched from OLT data.
  // If nothing is picked, the typed value is used as the serial (covers ONUs not on the OLT yet).
  const [labelQuery, setLabelQuery] = useState('');
  const [picked, setPicked]         = useState(null); // live ONU chosen from the label dropdown
  const serial = (preSerial || (picked ? (picked.serialNumber || picked.id || '') : labelQuery)).trim().toUpperCase();

  const preLabel = onu.label
    || liveOnus.find(o => (o.serialNumber || o.id || '').toUpperCase() === preSerial.toUpperCase())?.label
    || '';

  const labelMatches = useMemo(() => {
    const q = labelQuery.trim().toLowerCase();
    if (preSerial || picked || q.length < 2) return [];
    return liveOnus.filter(o => {
      // Mapping started from a PON node in the tree — only offer ONUs on that port
      if (onu._presetPon != null && o.port !== onu._presetPon) return false;
      const s = (o.serialNumber || o.id || '').toLowerCase();
      const l = (o.label || '').toLowerCase();
      return l.includes(q) || s.includes(q);
    }).slice(0, 8);
  }, [labelQuery, picked, liveOnus, preSerial, onu._presetPon]);

  // Per-splitter used ports index (exclude this ONU's own mapping to allow same-port re-select)
  const splitterUsage = useMemo(() => {
    const usage = {};
    for (const m of (mappings || [])) {
      const sid = m.splitterId?._id || (typeof m.splitterId === 'string' ? m.splitterId : null);
      if (!sid || !m.splitterPort) continue;
      // Skip this ONU's own mapping so its port isn't double-counted
      if (m.onuSerial === serial && sid === splitterId) continue;
      if (!usage[sid]) usage[sid] = [];
      usage[sid].push(Number(m.splitterPort));
    }
    return usage;
  }, [mappings, serial, splitterId]);

  const handleSplitterChange = (newSplitterId) => {
    setSplitterId(newSplitterId);
    if (!newSplitterId) { setSplitterPort(''); return; }
    const splitter = splitters.find(s => s._id === newSplitterId);
    const max = splitter ? ratioMax(splitter.ratio) : 64;
    const used = new Set(splitterUsage[newSplitterId] || []);
    // Find first available port
    let next = null;
    for (let p = 1; p <= max; p++) {
      if (!used.has(p)) { next = p; break; }
    }
    setSplitterPort(next != null ? String(next) : '');
  };

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await customerAPI.getAll({ search: q, limit: 8, customerType: 'broadband,both' });
      const list = res.data?.data?.docs || res.data?.data || [];
      setResults(list);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, doSearch]);

  const pickCustomer = (c) => {
    setCustomerId(c._id);
    const pppoe = c.broadband?.username ? ` · ${c.broadband.username}` : '';
    setCustomerName(`${c.name} (${c.customerId}${pppoe})`);
    setSearch('');
    setResults([]);
  };

  const handleSave = async () => {
    if (!serial) { toast.error('Pick an ONU by label or enter its serial number'); return; }
    setSaving(true);
    try {
      const res = await fiberAPI.saveMapping(serial, {
        splitterId:   splitterId || null,
        splitterPort: splitterPort ? +splitterPort : null,
        customerId:   customerId || null,
        ponPort:      onu.port ?? picked?.port ?? onu._presetPon ?? null,
        ponSlot:      0,
        onuId:        onu.onuId ?? picked?.onuId,
        onuGponId:    onu.id ?? picked?.id,
        notes,
      });
      toast.success('Mapping saved');
      const sync = res.data?.sync || {};
      if (sync.olt)  toast[sync.olt.includes('failed') ? 'error' : 'success'](`OLT: ${sync.olt}`, { duration: 5000 });
      if (sync.imbs) toast[sync.imbs.includes('failed') || sync.imbs.startsWith('no ') ? 'error' : 'success'](`Netzur: ${sync.imbs}`, { duration: 5000 });
      if (sync.note) toast(sync.note, { duration: 5000 });
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const maxPort = splitterId ? ratioMax(splitters.find(s => s._id === splitterId)?.ratio) : 64;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3>Map ONU</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {/* ONU — label first, serial fetched from live OLT data; read-only when from live OLT */}
          <div className="form-group">
            <label className="form-label">ONU Label <span className="required">*</span></label>
            {onu._presetPon != null && !preSerial && (
              <div style={{ fontSize: '.76rem', color: 'var(--c-1e40af)', background: 'var(--cb-dbeafe)', borderRadius: 6, padding: '5px 10px', marginBottom: 8 }}>
                Mapping on PON {onu._presetPon} — the label search only offers ONUs on this port.
              </div>
            )}
            {preSerial ? (
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', fontSize: '.82rem' }}>
                <div style={{ fontWeight: 700 }}>{preLabel || serial}</div>
                <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                  <span className="font-mono">SN: {serial}</span>
                  {onu.id && <span style={{ marginLeft: 10 }}>GPON ID: {onu.id}</span>}
                  {onu.port != null && <span style={{ marginLeft: 10 }}>PON Port: {onu.port}</span>}
                  {onu.model && <span style={{ marginLeft: 10 }}>Model: {onu.model}</span>}
                </div>
              </div>
            ) : picked ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, padding: '8px 12px', background: 'var(--cb-d1fae5)', borderRadius: 6, fontSize: '.83rem', color: 'var(--c-065f46)' }}>
                  <div style={{ fontWeight: 700 }}>{picked.label || '(no label)'}</div>
                  <div className="font-mono" style={{ fontSize: '.76rem', marginTop: 1 }}>
                    SN: {(picked.serialNumber || picked.id || '').toUpperCase()}
                    {picked.port != null && <span style={{ marginLeft: 8 }}>PON {picked.port}</span>}
                  </div>
                </div>
                <button onClick={() => { setPicked(null); setLabelQuery(''); }} className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }}>
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  className="form-control"
                  value={labelQuery}
                  onChange={e => setLabelQuery(e.target.value)}
                  placeholder="Type ONU label to search…"
                  autoFocus
                />
                {labelMatches.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.15)', maxHeight: 220, overflowY: 'auto',
                  }}>
                    {labelMatches.map((o, i) => (
                      <div key={i} onClick={() => setPicked(o)}
                        style={{ padding: '8px 14px', cursor: 'pointer', fontSize: '.83rem', borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <span style={{ fontWeight: 600 }}>{o.label || '(no label)'}</span>
                        <span className="font-mono" style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: '.76rem' }}>
                          {(o.serialNumber || o.id || '').toUpperCase()}
                        </span>
                        {o.port != null && <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: '.76rem' }}>PON {o.port}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Pick by label — the ONU serial number is fetched automatically from the OLT.
                  If nothing matches, the typed value is used as the serial.
                </div>
              </div>
            )}
          </div>

          {/* Customer search */}
          <div className="form-group" style={{ position: 'relative' }}>
            <label className="form-label">Customer</label>
            {customerId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, padding: '7px 12px', background: 'var(--cb-d1fae5)', borderRadius: 6, fontSize: '.83rem', fontWeight: 600, color: 'var(--c-065f46)' }}>
                  <User size={12} style={{ marginRight: 5 }} />{customerName}
                </div>
                <button onClick={() => { setCustomerId(''); setCustomerName(''); }} className="btn btn-secondary btn-sm" style={{ padding: '4px 8px' }}>
                  <X size={12} />
                </button>
              </div>
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                  <input className="form-control" style={{ paddingLeft: 30 }} placeholder="Search by name, mobile, account…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                {(searching || results.length > 0) && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,.15)', maxHeight: 200, overflowY: 'auto',
                  }}>
                    {searching && <div style={{ padding: '8px 12px', fontSize: '.8rem', color: 'var(--text-muted)' }}>Searching…</div>}
                    {results.map(c => (
                      <div key={c._id} onClick={() => pickCustomer(c)} style={{
                        padding: '8px 14px', cursor: 'pointer', fontSize: '.83rem',
                        borderBottom: '1px solid var(--border)',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                        <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: '.78rem' }}>{c.customerId} · {c.mobile}</span>
                        {c.broadband?.username && (
                          <span style={{ fontFamily: 'monospace', fontSize: '.76rem', color: 'var(--primary)', marginLeft: 8 }}>
                            {c.broadband.username}
                          </span>
                        )}
                      </div>
                    ))}
                    {!searching && results.length === 0 && search.length >= 2 && (
                      <div style={{ padding: '8px 14px', fontSize: '.8rem', color: 'var(--text-muted)' }}>No customers found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Splitter */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Splitter</label>
              <SplitterPicker
                splitters={splitters}
                splitterUsage={splitterUsage}
                value={splitterId}
                onChange={handleSplitterChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Splitter Port</label>
              <input className="form-control" type="number" min="1" max={maxPort} value={splitterPort}
                onChange={e => setSplitterPort(e.target.value)} placeholder={`1–${maxPort}`}
                disabled={!splitterId} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes" />
          </div>
        </div>
        <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
// Reconstructed around the map.html reference: the whole page IS the fiber map (OLT →
// splitters → ONUs on a Leaflet map with a Faults sidebar), plus a "Network Structure"
// page-level tab (GeoMapPanel's pageView) for the full-width PON/splitter/ONU tree —
// not buried among separate Tree/PON-Ports/Splitters/Map-ONUs list views like before. Splitter
// and PON-port config (real backend entities, not map.html's freeform add-anything model)
// stay reachable via small admin utilities (Add Splitter, Manage PON Ports) rather than a
// map-click "+ Splitter" tool — see GeoMapPanel for OLT/splitter/ONU placement itself.
export default function FiberMapPage() {
  useDarkReady();
  // Delete actions match the backend's technicalOrAdmin gate.
  const { canManageHardware: isAdmin, isTechnical } = useAuth();
  // Field-tech GPS view: read-only map (no drag/place/route-trace, no cable cost figures),
  // just the live status/faults a tech needs on site. A technical-role login only ever
  // reaches this page via /technical/fiber-map, so it always starts in field view; a real
  // admin can flip the toggle to preview it.
  const [fieldView, setFieldView] = useState(isTechnical);

  // Tree (feeds the map's "Network Structure" page-level tab)
  const [tree, setTree]           = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treePon, setTreePon]     = useState('all');

  // PON Ports (admin utility modal only)
  const [ponPorts, setPonPorts]           = useState([]);
  const [ponPortsLoading, setPonPortsLoading] = useState(false);
  const [ponPortModal, setPonPortModal]   = useState(null); // null | {} | ponPortObj
  const [ponPortsListOpen, setPonPortsListOpen] = useState(false);

  // Splitters
  const [splitters, setSplitters]         = useState([]);
  const [splitterModal, setSplitterModal] = useState(null); // null | {} | splitterObj

  // ONU mappings
  const [mappings, setMappings]       = useState([]);
  const [liveOnus, setLiveOnus]       = useState([]);
  const [oltLoading, setOltLoading]   = useState(false);
  const [oltError, setOltError]       = useState(null);
  const [mapModal, setMapModal]       = useState(null); // onu object

  // Live ONU map (serial → onu) for tree/map cross-reference
  const [liveMap, setLiveMap] = useState({});

  const fetchPONPorts = useCallback(async () => {
    setPonPortsLoading(true);
    try {
      const res = await fiberAPI.getPONPorts();
      setPonPorts(res.data?.data || []);
    } catch { toast.error('Failed to load PON ports'); }
    finally { setPonPortsLoading(false); }
  }, []);

  const fetchTree = useCallback(async () => {
    setTreeLoading(true);
    try {
      const res = await fiberAPI.getTree();
      setTree(res.data?.data || { ports: [] });
    } catch { toast.error('Failed to load fiber tree'); }
    finally { setTreeLoading(false); }
  }, []);

  const fetchSplitters = useCallback(async () => {
    try {
      const res = await fiberAPI.getSplitters();
      setSplitters(res.data?.data || []);
    } catch { toast.error('Failed to load splitters'); }
  }, []);

  const fetchMappings = useCallback(async () => {
    try {
      const res = await fiberAPI.getMappings();
      setMappings(res.data?.data || []);
    } catch { toast.error('Failed to load mappings'); }
  }, []);

  const fetchLiveOnus = useCallback(async () => {
    setOltLoading(true);
    setOltError(null);
    try {
      const res = await oltAPI.getONUs();
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setLiveOnus(list);
      const m = {};
      for (const o of list) {
        const serial = (o.serialNumber || o.id || '').toUpperCase();
        if (serial) m[serial] = o;
      }
      setLiveMap(m);
    } catch (err) {
      setOltError(err.response?.data?.message || 'Cannot reach OLT — check Settings');
    } finally { setOltLoading(false); }
  }, []);

  const refreshAll = useCallback(() => {
    fetchTree(); fetchPONPorts(); fetchSplitters(); fetchMappings(); fetchLiveOnus();
  }, [fetchTree, fetchPONPorts, fetchSplitters, fetchMappings, fetchLiveOnus]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Stats
  const totalSplitters = splitters.length;
  const totalMapped    = mappings.length;
  const totalCustomers = mappings.filter(m => m.customerId).length;

  // ── Sync live ONUs → Device Inventory ──
  const [syncing, setSyncing] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);
  const [fetchingOnus, setFetchingOnus] = useState(false);

  // Adds a placeholder Fiber Map row for any OLT-registered ONU the map doesn't know about
  // yet at all (no customer/splitter needed) — same sync the background cron runs every 10
  // min, just on demand. Purely additive; never touches an ONU that's already mapped.
  const handleFetchNewOnus = async () => {
    setFetchingOnus(true);
    try {
      const { data } = await fiberAPI.syncOnus();
      toast.success(data.message || `${data.data?.added || 0} new ONU(s) fetched`, { duration: 6000 });
      fetchMappings();
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Fetch new ONUs failed');
    } finally {
      setFetchingOnus(false);
    }
  };

  // Match every unlinked ONU's OLT label against a PPPoE username (local or Netzur) and link it.
  const handleAutoLink = async () => {
    setAutoLinking(true);
    try {
      const { data } = await fiberAPI.autoLink();
      const r = data.data || {};
      toast.success(data.message || `${r.linked || 0} ONU(s) linked`, { duration: 6000 });
      if (r.noMatch?.length) {
        toast(`No matching username for: ${r.noMatch.slice(0, 5).join(', ')}${r.noMatch.length > 5 ? ` +${r.noMatch.length - 5} more` : ''}`,
          { icon: 'ℹ️', duration: 8000 });
      }
      fetchMappings();
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto link failed');
    } finally {
      setAutoLinking(false);
    }
  };

  const handleSyncToInventory = async () => {
    if (liveOnus.length === 0) {
      toast.error('No live ONU data — reload the page first to fetch from OLT');
      return;
    }
    setSyncing(true);
    try {
      const [cfgRes] = await Promise.allSettled([settingsAPI.getOLT()]);
      const oltCfg = cfgRes.status === 'fulfilled' ? cfgRes.value.data?.data : null;
      const mapIndex = {};
      for (const m of mappings) mapIndex[m.onuSerial] = m;

      const toSync = liveOnus.map(o => {
        const serial = (o.serialNumber || o.id || '').toUpperCase();
        const map = mapIndex[serial];
        const custId = map?.customerId?._id || (typeof map?.customerId === 'string' ? map.customerId : null);
        return {
          serialNumber: serial,
          gponId:       o.id || o.onuGponId || '',
          deviceType: 'onu',
          model: o.model || 'ONU',
          status: 'installed',
          olt: { name: oltCfg?.name || '', ip: oltCfg?.ip || '', ponPort: o.port != null ? String(o.port) : (map?.ponPort != null ? String(map.ponPort) : '') },
          splitter: map ? { name: map.splitterId?.name || '', location: map.splitterId?.location || '', port: map.splitterPort != null ? Number(map.splitterPort) : null } : {},
          ...(custId ? { customer: custId } : {}),
        };
      }).filter(d => d.serialNumber);

      const { data } = await deviceAPI.bulkSync(toSync);
      toast.success(`${data.data.created} added · ${data.data.updated} updated in Device Inventory`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    } finally { setSyncing(false); }
  };

  // ── Delete splitter ──
  const handleDeleteSplitter = async (s) => {
    if (!window.confirm(`Delete splitter "${s.name}"? ONUs linked to it will become unmapped.`)) return;
    try {
      await fiberAPI.deleteSplitter(s._id);
      toast.success('Splitter deleted');
      fetchSplitters();
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // ── Delete a whole PON (splitters + mappings + config entry) ──
  const handleDeletePONTree = async (port) => {
    const totalOnus = (port.splitters || []).reduce((s, sp) => s + countTreeOnus(sp), 0)
      + (port.unmappedOnus?.length || 0);
    if (!window.confirm(
      `Delete PON ${port.ponPort} completely?\n\nThis removes ${port.splitters?.length || 0} splitter(s) and ${totalOnus} ONU mapping(s) on this port. Customers themselves are not affected.`
    )) return;
    try {
      const { data } = await fiberAPI.deletePONPortTree(port.ponPort);
      toast.success(data.message || `PON ${port.ponPort} deleted`);
      fetchTree();
      fetchSplitters();
      fetchMappings();
      fetchPONPorts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  // ── Delete mapping ──
  const handleDeleteMapping = async (serial) => {
    if (!window.confirm(`Remove mapping for ${serial}?`)) return;
    try {
      await fiberAPI.deleteMapping(serial);
      toast.success('Mapping removed');
      fetchMappings();
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Remove failed');
    }
  };

  // ── Delete PON port (config entry only) ──
  const handleDeletePONPort = async (p) => {
    if (!window.confirm(`Remove PON Port ${p.port} (${p.name || 'unnamed'}) from config? Splitters on this port are not affected.`)) return;
    try {
      await fiberAPI.deletePONPort(p.port);
      toast.success('PON port removed');
      fetchPONPorts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="fm-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Fiber Mapping</h2>
        <div className="fm-btns" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {!isTechnical && (
            <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 6, padding: 2 }}>
              <button className="btn btn-sm" onClick={() => setFieldView(false)} style={{
                border: 'none', borderRadius: 5, padding: '5px 10px',
                background: !fieldView ? 'var(--primary)' : 'transparent',
                color: !fieldView ? 'var(--c-ffffff)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5,
              }} title="Full admin view — edit, drag pins, trace routes, see costs">
                <ShieldCheck size={13} />Admin
              </button>
              <button className="btn btn-sm" onClick={() => setFieldView(true)} style={{
                border: 'none', borderRadius: 5, padding: '5px 10px',
                background: fieldView ? 'var(--primary)' : 'transparent',
                color: fieldView ? 'var(--c-ffffff)' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5,
              }} title="Field-tech view — read only, no edit/cost figures">
                <HardHat size={13} />Field tech
              </button>
            </div>
          )}
          <button className="btn btn-secondary btn-sm" onClick={refreshAll}>
            <RefreshCw size={13} style={{ marginRight: 5 }} />Refresh
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleFetchNewOnus} disabled={fetchingOnus}
            title="Add any OLT-registered ONU the Fiber Map doesn't know about yet (unmapped, no customer needed)">
            <RadioTower size={13} style={{ marginRight: 5 }} />{fetchingOnus ? 'Fetching…' : 'Fetch New ONUs'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleAutoLink} disabled={autoLinking}
            title="Link every unlinked ONU whose OLT label matches a customer's PPPoE username (local or Netzur)">
            <Link2 size={13} style={{ marginRight: 5 }} />{autoLinking ? 'Linking…' : 'Auto Link'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleSyncToInventory} disabled={syncing || liveOnus.length === 0}>
            <Download size={13} style={{ marginRight: 5 }} />{syncing ? 'Syncing…' : 'Sync to Inventory'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setPonPortsListOpen(true); fetchPONPorts(); }}>
            <Server size={13} style={{ marginRight: 5 }} />Manage PON Ports
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setSplitterModal({})}>
            <Plus size={13} style={{ marginRight: 5 }} />Add Splitter
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setMapModal({ _manual: true })}>
            <Plus size={13} style={{ marginRight: 5 }} />Add ONU Mapping
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="stats-grid fm-pagestats" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-ede9fe)' }}><GitBranch size={20} style={{ color: 'var(--c-5b21b6)' }} /></div>
          <div className="stat-info"><p>Splitters</p><h3>{totalSplitters}</h3></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-dbeafe)' }}><Wifi size={20} style={{ color: 'var(--c-1e40af)' }} /></div>
          <div className="stat-info"><p>Mapped ONUs</p><h3>{totalMapped}</h3></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-d1fae5)' }}><Users size={20} style={{ color: 'var(--c-065f46)' }} /></div>
          <div className="stat-info"><p>Customers Linked</p><h3>{totalCustomers}</h3></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'var(--cb-fef3c7)' }}><WifiOff size={20} style={{ color: 'var(--c-92400e)' }} /></div>
          <div className="stat-info">
            <p>Live ONUs</p>
            <h3>{oltLoading ? '—' : liveOnus.length}</h3>
          </div>
        </div>
      </div>

      {oltError && (
        <div style={{
          margin: '0 0 16px', padding: '8px 14px', background: 'var(--cb-fef3c7)',
          border: '1px solid var(--cb-fbbf24)', borderRadius: 6, fontSize: '.82rem',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={14} style={{ color: 'var(--c-92400e)' }} />
          <span style={{ color: 'var(--c-92400e)' }}>{oltError} — showing stored mappings only.</span>
        </div>
      )}

      {/* ── The map — the whole page's reason for being, per the map.html reference ── */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <GeoMapPanel
            splitters={splitters}
            mappings={mappings}
            liveMap={liveMap}
            isAdmin={isAdmin}
            fieldView={fieldView}
            onRefreshSplitters={fetchSplitters}
            onRefreshMappings={fetchMappings}
            tree={tree}
            treeLoading={treeLoading}
            treePon={treePon}
            setTreePon={setTreePon}
            onEditSplitter={s => setSplitterModal(s)}
            onDeleteSplitter={handleDeleteSplitter}
            onEditOnu={o => setMapModal({
              serialNumber: o.onuSerial, _serial: o.onuSerial,
              id: o.onuGponId, onuGponId: o.onuGponId,
              port: o.ponPort, onuId: o.onuId,
              _map: o,
            })}
            onDeleteOnu={isAdmin ? handleDeleteMapping : undefined}
            onAddOnu={sid => setMapModal({ _manual: true, _defaultSplitterId: sid })}
            onMapOnu={pon => setMapModal({ _manual: true, _presetPon: pon })}
            onDeletePort={isAdmin ? handleDeletePONTree : undefined}
          />
        </div>
      </div>

      {/* ── Modals ── */}
      {ponPortsListOpen && (
        <PonPortsManagerModal
          ponPorts={ponPorts}
          loading={ponPortsLoading}
          onClose={() => setPonPortsListOpen(false)}
          onAdd={() => setPonPortModal({})}
          onEdit={p => setPonPortModal(p)}
          onDelete={handleDeletePONPort}
        />
      )}
      {ponPortModal !== null && (
        <PONPortModal
          ponPort={ponPortModal?.port != null ? ponPortModal : null}
          onClose={() => setPonPortModal(null)}
          onSaved={() => { fetchPONPorts(); fetchTree(); }}
        />
      )}
      {splitterModal !== null && (
        <SplitterModal
          splitter={splitterModal?._id ? splitterModal : null}
          splitters={splitters}
          onClose={() => setSplitterModal(null)}
          onSaved={() => { fetchSplitters(); fetchTree(); }}
        />
      )}
      {mapModal && (
        <MapModal
          onu={mapModal}
          splitters={splitters}
          existingMap={mapModal._map}
          mappings={mappings}
          liveOnus={liveOnus}
          defaultSplitterId={mapModal._defaultSplitterId || ''}
          onClose={() => setMapModal(null)}
          onSaved={() => { fetchMappings(); fetchTree(); }}
        />
      )}
    </div>
  );
}

function countTreeOnus(sp) {
  return (sp.onus?.length || 0) + (sp.children || []).reduce((s, c) => s + countTreeOnus(c), 0);
}
